import React from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiUrl } from "@/lib/apiUrl";

interface Props {
  children: React.ReactNode;
  campaignId?: number;
  companyId?: number;
  channelId?: number;
}

interface State {
  hasError: boolean;
  errorId: string | null;
  reported: boolean;
  isHarmless: boolean;
}

/**
 * Error Boundary específico para o questionário público.
 * 
 * REGRAS:
 * 1. NUNCA exibir stack trace ou mensagem técnica para o usuário final
 * 2. SEMPRE reportar o erro ao servidor (módulo de logs)
 * 3. Erros de removeChild (race condition do Radix UI) são silenciados sem mostrar tela de erro
 * 4. Outros erros exibem mensagem amigável com código de protocolo
 */
export class QuestionnaireErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorId: null, reported: false, isHarmless: false };
  }

  /**
   * Verifica se o erro é o removeChild race condition (inofensivo).
   * Esses erros ocorrem quando o Radix UI tenta remover um nó DOM
   * que já foi removido por outro ciclo de renderização.
   */
  private static isHarmlessError(error: Error): boolean {
    const msg = error?.message || "";
    const name = error?.name || "";
    return (
      msg.includes("removeChild") ||
      msg.includes("NotFoundError") ||
      name === "NotFoundError" ||
      msg.includes("The node to be removed is not a child of this node") ||
      msg.includes("O nó a ser removido não é filho deste nó") ||
      msg.includes("insertBefore") ||
      msg.includes("appendChild")
    );
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const isHarmless = QuestionnaireErrorBoundary.isHarmlessError(error);
    
    // Se for o erro inofensivo de removeChild, NÃO mostrar tela de erro
    if (isHarmless) {
      return { hasError: false, errorId: null, reported: false, isHarmless: true };
    }
    
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, errorId, reported: false, isHarmless: false };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const isHarmless = QuestionnaireErrorBoundary.isHarmlessError(error);
    
    // Log no console apenas em desenvolvimento
    if (process.env.NODE_ENV === "development") {
      if (isHarmless) {
        console.warn("[QuestionnaireErrorBoundary] Harmless DOM error silenced:", error.message);
      } else {
        console.error("[QuestionnaireErrorBoundary] Caught error:", error, info);
      }
    }

    // SEMPRE reportar ao servidor — mesmo erros inofensivos, para rastreabilidade
    const errorId = this.state.errorId || `ERR-${Date.now().toString(36).toUpperCase()}`;
    this.reportError(error, info, errorId, isHarmless);
  }

  private async reportError(error: Error, info: React.ErrorInfo, errorId: string, isHarmless: boolean) {
    try {
      await fetch(apiUrl("/api/trpc/systemErrors.logPublicError"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          json: {
            errorType: "QUESTIONNAIRE",
            errorMessage: `[${errorId}] ${isHarmless ? "[HARMLESS] " : ""}${error.message || "Unknown error"}`.substring(0, 2000),
            errorStack: error.stack?.substring(0, 10000),
            componentStack: info.componentStack?.substring(0, 10000),
            url: window.location.href.substring(0, 512),
            userAgent: navigator.userAgent.substring(0, 512),
            campaignId: this.props.campaignId,
            companyId: this.props.companyId,
            channelId: this.props.channelId,
            context: {
              errorId,
              timestamp: new Date().toISOString(),
              isHarmless,
              source: "QuestionnaireErrorBoundary",
            },
          },
        }),
      });
      this.setState({ reported: true });
    } catch {
      // Silencioso — nunca propagar erro do error boundary
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // NUNCA exibir stack trace — apenas mensagem amigável
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
          {/* Ícone */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-amber-50 flex items-center justify-center">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
          </div>

          {/* Título */}
          <h1 className="text-xl font-semibold text-gray-900 mb-3">
            Algo deu errado
          </h1>

          {/* Mensagem amigável — SEM stack trace, SEM mensagem técnica */}
          <p className="text-gray-600 text-sm leading-relaxed mb-2">
            Ocorreu um problema ao carregar o questionário. Isso pode ter sido causado por uma instabilidade temporária de conexão.
          </p>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">
            <strong>Suas respostas anteriores não foram perdidas.</strong> Tente recarregar a página para continuar de onde parou.
          </p>

          {/* Código de protocolo */}
          {this.state.errorId && (
            <div className="bg-gray-50 rounded-lg px-4 py-3 mb-6 text-left">
              <p className="text-xs text-gray-500 mb-1">Código de protocolo</p>
              <p className="text-sm font-mono font-medium text-gray-700">{this.state.errorId}</p>
              <p className="text-xs text-gray-400 mt-1">
                Informe este código ao RH da sua empresa caso o problema persista.
              </p>
            </div>
          )}

          {/* Ações */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={this.handleReload}
              className="w-full gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Recarregar página
            </Button>
          </div>

          {/* Status de reporte */}
          {this.state.reported && (
            <p className="text-xs text-gray-400 mt-4">
              O problema foi reportado automaticamente para a equipe técnica.
            </p>
          )}
        </div>
      </div>
    );
  }
}
